import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import Autocomplete from '../Common/Autocomplete';
import { productService } from '../../api/productService';
import '../Common/Common.css';

function CouponFormModal({ isOpen, onClose, onSubmit, coupon = null }) {
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    description: '',
    scope: 'order',
    product_id: '',
    min_order_amount: '',
    max_uses: '',
    max_uses_per_user: '',
    starts_at: '',
    expires_at: '',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState([]);

  // Load the catalog once the modal opens so the product picker is populated.
  useEffect(() => {
    if (!isOpen) return;

    let active = true;
    (async () => {
      try {
        const response = await productService.getProducts({ per_page: 1000 });
        if (active) setProducts(response.data || []);
      } catch (error) {
        console.error('Error loading products for coupon form:', error);
      }
    })();

    return () => { active = false; };
  }, [isOpen]);

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || '',
        type: coupon.type || 'percentage',
        value: coupon.value || '',
        description: coupon.description || '',
        scope: coupon.product_id ? 'product' : 'order',
        product_id: coupon.product_id ? String(coupon.product_id) : '',
        min_order_amount: coupon.min_order_amount || '',
        max_uses: coupon.max_uses || '',
        max_uses_per_user: coupon.max_uses_per_user || '',
        starts_at: coupon.starts_at ? formatDateOnly(coupon.starts_at) : '',
        expires_at: coupon.expires_at ? formatDateOnly(coupon.expires_at) : '',
        is_active: coupon.is_active !== undefined ? coupon.is_active : true,
      });
    } else {
      setFormData({
        code: '',
        type: 'percentage',
        value: '',
        description: '',
        scope: 'order',
        product_id: '',
        min_order_amount: '',
        max_uses: '',
        max_uses_per_user: '',
        starts_at: '',
        expires_at: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [coupon, isOpen]);

  // YYYY-MM-DD — matches <input type="date">
  const formatDateOnly = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'code') {
      // Convert to uppercase and allow only alphanumeric, dashes, and underscores
      const sanitizedValue = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est requis';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.code)) {
      newErrors.code = 'Le code ne peut contenir que des lettres majuscules, chiffres, tirets et underscores';
    }

    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }

    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'La valeur doit être supérieure à 0';
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Le pourcentage ne peut pas dépasser 100%';
    }

    if (formData.scope === 'product' && !formData.product_id) {
      newErrors.product_id = 'Veuillez sélectionner un produit';
    }

    if (formData.min_order_amount && formData.min_order_amount < 0) {
      newErrors.min_order_amount = 'Le montant minimum doit être positif';
    }

    if (formData.max_uses && formData.max_uses < 1) {
      newErrors.max_uses = 'Le nombre maximum d\'utilisations doit être au moins 1';
    }

    if (formData.max_uses_per_user && formData.max_uses_per_user < 1) {
      newErrors.max_uses_per_user = 'Le nombre maximum d\'utilisations par utilisateur doit être au moins 1';
    }

    if (formData.starts_at && formData.expires_at) {
      if (new Date(formData.starts_at) >= new Date(formData.expires_at)) {
        newErrors.expires_at = 'La date d\'expiration doit être après la date de début';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data for submission
      const submitData = {
        code: formData.code,
        type: formData.type,
        value: parseFloat(formData.value),
        product_id: formData.scope === 'product' ? parseInt(formData.product_id) : null,
        description: formData.description || null,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        starts_at: formData.starts_at || null,
        expires_at: formData.expires_at || null,
        is_active: formData.is_active,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting coupon:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={coupon ? 'Modifier le coupon' : 'Ajouter un coupon'} size="large">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Code *</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="EX: PROMO2025"
              disabled={submitting}
            />
            {errors.code && <div className="error-text">{errors.code}</div>}
          </div>

          <div className="form-group">
            <label>Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="percentage">Pourcentage</option>
              <option value="fixed">Montant fixe</option>
            </select>
            {errors.type && <div className="error-text">{errors.type}</div>}
          </div>

          <div className="form-group">
            <label>Valeur * {formData.type === 'percentage' ? '(%)' : '(DH)'}</label>
            <input
              type="number"
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder={formData.type === 'percentage' ? '10' : '50'}
              step="0.01"
              min="0"
              disabled={submitting}
            />
            {errors.value && <div className="error-text">{errors.value}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description du coupon..."
              rows="3"
              disabled={submitting}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #cbd5e0',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            {errors.description && <div className="error-text">{errors.description}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Appliquer à *</label>
            <select
              name="scope"
              value={formData.scope}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="order">Toute la commande</option>
              <option value="product">Produit spécifique</option>
            </select>
          </div>

          {formData.scope === 'product' && (
            <div className="form-group" style={{ gridColumn: '2 / -1' }}>
              <label>Produit *</label>
              <Autocomplete
                options={products.map((p) => ({
                  id: String(p.id),
                  displayName: `${p.name} (${parseFloat(p.price).toFixed(2)} DH)`,
                }))}
                value={formData.product_id}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, product_id: value }));
                  if (errors.product_id) setErrors((prev) => ({ ...prev, product_id: '' }));
                }}
                placeholder="Rechercher un produit..."
                displayKey="displayName"
                valueKey="id"
              />
              {errors.product_id && <div className="error-text">{errors.product_id}</div>}
              <small style={{ color: '#718096', fontSize: '12px' }}>
                La réduction s'applique uniquement au montant total de ce produit dans le panier.
              </small>
            </div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Montant minimum de commande (DH)</label>
            <input
              type="number"
              name="min_order_amount"
              value={formData.min_order_amount}
              onChange={handleChange}
              placeholder="Ex: 500"
              step="0.01"
              min="0"
              disabled={submitting}
            />
            {errors.min_order_amount && <div className="error-text">{errors.min_order_amount}</div>}
          </div>

          <div className="form-group">
            <label>Utilisations max (total)</label>
            <input
              type="number"
              name="max_uses"
              value={formData.max_uses}
              onChange={handleChange}
              placeholder="Ex: 100"
              min="1"
              disabled={submitting}
            />
            {errors.max_uses && <div className="error-text">{errors.max_uses}</div>}
          </div>

          <div className="form-group">
            <label>Utilisations max par utilisateur</label>
            <input
              type="number"
              name="max_uses_per_user"
              value={formData.max_uses_per_user}
              onChange={handleChange}
              placeholder="Ex: 1"
              min="1"
              disabled={submitting}
            />
            {errors.max_uses_per_user && <div className="error-text">{errors.max_uses_per_user}</div>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Date de début</label>
            <input
              type="date"
              name="starts_at"
              value={formData.starts_at}
              onChange={handleChange}
              disabled={submitting}
            />
            {errors.starts_at && <div className="error-text">{errors.starts_at}</div>}
          </div>

          <div className="form-group">
            <label>Date d'expiration</label>
            <input
              type="date"
              name="expires_at"
              value={formData.expires_at}
              onChange={handleChange}
              min={formData.starts_at || undefined}
              disabled={submitting}
            />
            {errors.expires_at && <div className="error-text">{errors.expires_at}</div>}
          </div>

          <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={submitting}
                style={{ width: 'auto', marginRight: '8px' }}
              />
              <span>Actif</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={submitting}>
            Annuler
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Enregistrement...' : coupon ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CouponFormModal;
